import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFeatureFlags } from '../context/FeatureFlagsContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import AppShell from '../components/AppShell';
import {
  Map,
  Rocket,
  Zap,
  Crown,
  CheckCircle,
  Clock,
  AlertCircle,
  Lock,
  Unlock,
  ChevronRight,
  Settings,
  Eye,
  EyeOff,
  Play,
  Pause,
  RefreshCw,
  Filter,
  Search
} from 'lucide-react';
import { Input } from '../components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

// Status colors and icons
const statusConfig = {
  released: { 
    color: 'bg-green-500/20 text-green-400 border-green-500/30', 
    icon: CheckCircle, 
    label: 'Released' 
  },
  beta: { 
    color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', 
    icon: Zap, 
    label: 'Beta' 
  },
  in_progress: { 
    color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', 
    icon: Clock, 
    label: 'In Progress' 
  },
  planned: { 
    color: 'bg-muted text-muted-foreground border-border', 
    icon: AlertCircle, 
    label: 'Planned' 
  },
  deprecated: { 
    color: 'bg-red-500/20 text-red-400 border-red-500/30', 
    icon: AlertCircle, 
    label: 'Deprecated' 
  }
};

// Phase icons
const phaseIcons = {
  phase_1: Rocket,
  phase_2: Zap,
  phase_3: Crown
};

// Tier badge colors
const tierColors = {
  starter: 'bg-gray-500/20 text-gray-400',
  standard: 'bg-blue-500/20 text-blue-400',
  pro: 'bg-purple-500/20 text-purple-400',
  elite: 'bg-amber-500/20 text-amber-400',
  agency: 'bg-pink-500/20 text-pink-400',
  studio: 'bg-emerald-500/20 text-emerald-400'
};

const RoadmapPage = () => {
  const { token, user } = useAuth();
  const { fetchRoadmap } = useFeatureFlags();
  
  const [roadmap, setRoadmap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedPhases, setExpandedPhases] = useState(['phase_1', 'phase_2', 'phase_3']);

  useEffect(() => {
    loadRoadmap();
  }, []);

  const loadRoadmap = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/features/roadmap`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoadmap(response.data);
    } catch (error) {
      console.error('Failed to load roadmap:', error);
      toast.error('Failed to load roadmap');
    } finally {
      setLoading(false);
    }
  };

  const togglePhase = (phaseId) => {
    setExpandedPhases(prev => 
      prev.includes(phaseId) 
        ? prev.filter(p => p !== phaseId) 
        : [...prev, phaseId]
    );
  };

  const toggleFeatureFlag = async (flagId, enabled) => {
    if (!user?.is_founder) {
      toast.error('Admin access required');
      return;
    }
    
    try {
      await axios.put(`${API}/api/features/flags/${flagId}`, 
        { enabled: !enabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Feature ${!enabled ? 'enabled' : 'disabled'}`);
      loadRoadmap();
    } catch (error) {
      toast.error('Failed to update feature flag');
    }
  };

  const toggleVisibility = async (flagId, visible) => {
    if (!user?.is_founder) {
      toast.error('Admin access required');
      return;
    }
    
    try {
      await axios.put(`${API}/api/features/flags/${flagId}`, 
        { visible: !visible },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Feature ${!visible ? 'shown' : 'hidden'} from users`);
      loadRoadmap();
    } catch (error) {
      toast.error('Failed to update visibility');
    }
  };

  // Filter features
  const filterFeatures = (features) => {
    return features.filter(feature => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          feature.name?.toLowerCase().includes(query) ||
          feature.description?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      // Status filter
      if (statusFilter !== 'all' && feature.status !== statusFilter) {
        return false;
      }
      
      return true;
    });
  };

  // Feature Card Component
  const FeatureCard = ({ feature }) => {
    const status = statusConfig[feature.status] || statusConfig.planned;
    const StatusIcon = status.icon;
    
    return (
      <div className="p-4 bg-card border border-border rounded-xl hover:border-primary/30 transition-all">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded-full text-xs border ${status.color}`}>
              <StatusIcon className="w-3 h-3 inline mr-1" />
              {status.label}
            </span>
            <span className={`px-2 py-1 rounded-full text-xs ${tierColors[feature.tier_required] || tierColors.starter}`}>
              {feature.tier_required}
            </span>
          </div>
          
          {/* Admin Controls */}
          {user?.is_founder && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleFeatureFlag(feature.id, feature.enabled)}
                className={`h-7 w-7 p-0 ${feature.enabled ? 'text-green-400' : 'text-muted-foreground'}`}
                title={feature.enabled ? 'Disable feature' : 'Enable feature'}
              >
                {feature.enabled ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => toggleVisibility(feature.id, feature.visible)}
                className={`h-7 w-7 p-0 ${feature.visible ? 'text-blue-400' : 'text-muted-foreground'}`}
                title={feature.visible ? 'Hide from users' : 'Show to users'}
              >
                {feature.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </Button>
            </div>
          )}
        </div>
        
        <h3 className="font-semibold mb-1">{feature.name}</h3>
        <p className="text-sm text-muted-foreground mb-3">{feature.description}</p>
        
        {/* Additional Info */}
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {feature.release_date && (
            <span className="text-muted-foreground">
              Released: {feature.release_date}
            </span>
          )}
          {feature.blocked_reason && (
            <span className="text-red-400 flex items-center gap-1">
              <Lock className="w-3 h-3" />
              {feature.blocked_reason}
            </span>
          )}
          {feature.beta_note && (
            <span className="text-blue-400">{feature.beta_note}</span>
          )}
          {feature.dependencies?.length > 0 && (
            <span className="text-muted-foreground">
              Depends on: {feature.dependencies.join(', ')}
            </span>
          )}
        </div>
      </div>
    );
  };

  // Phase Section Component
  const PhaseSection = ({ phaseId, phase, features }) => {
    const PhaseIcon = phaseIcons[phaseId] || Rocket;
    const isExpanded = expandedPhases.includes(phaseId);
    const filteredFeatures = filterFeatures(features);
    
    const phaseStatusCounts = {
      released: features.filter(f => f.status === 'released').length,
      beta: features.filter(f => f.status === 'beta').length,
      in_progress: features.filter(f => f.status === 'in_progress').length,
      planned: features.filter(f => f.status === 'planned').length
    };
    
    return (
      <div className="mb-8">
        {/* Phase Header */}
        <button
          onClick={() => togglePhase(phaseId)}
          className="w-full flex items-center justify-between p-4 bg-card border border-border rounded-xl hover:border-primary/30 transition-all mb-4"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-primary/20">
              <PhaseIcon className="w-6 h-6 text-primary" />
            </div>
            <div className="text-left">
              <h2 className="font-bold text-lg">{phase.name}</h2>
              <p className="text-sm text-muted-foreground">{phase.description}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Status counts */}
            <div className="hidden md:flex items-center gap-2">
              {phaseStatusCounts.released > 0 && (
                <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-400">
                  {phaseStatusCounts.released} released
                </span>
              )}
              {phaseStatusCounts.beta > 0 && (
                <span className="px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400">
                  {phaseStatusCounts.beta} beta
                </span>
              )}
              {phaseStatusCounts.planned > 0 && (
                <span className="px-2 py-1 rounded-full text-xs bg-muted text-muted-foreground">
                  {phaseStatusCounts.planned} planned
                </span>
              )}
            </div>
            
            <span className={`px-3 py-1 rounded-full text-xs ${
              phase.status === 'released' ? 'bg-green-500/20 text-green-400' :
              phase.status === 'in_progress' ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-muted text-muted-foreground'
            }`}>
              {phase.target_date}
            </span>
            
            <ChevronRight className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          </div>
        </button>
        
        {/* Phase Features */}
        {isExpanded && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pl-4">
            {filteredFeatures.length > 0 ? (
              filteredFeatures.map(feature => (
                <FeatureCard key={feature.id} feature={feature} />
              ))
            ) : (
              <div className="col-span-full text-center py-8 text-muted-foreground">
                No features match your filters
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="p-5 sm:p-7 lg:p-10">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-10">
          <div>
            <p className="text-[10px] text-primary/60 uppercase tracking-[0.2em] font-semibold mb-3">Roadmap</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
              Product Roadmap
            </h1>
            <p className="text-zinc-500 text-sm mt-2">
              Track feature development and upcoming releases
            </p>
          </div>
          
          <Button
            variant="outline"
            onClick={loadRoadmap}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>
        
        {/* Summary Stats */}
        {roadmap?.summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="p-4 bg-card border border-border rounded-xl">
              <div className="text-2xl font-bold text-green-400">{roadmap.summary.released}</div>
              <div className="text-sm text-muted-foreground">Released</div>
            </div>
            <div className="p-4 bg-card border border-border rounded-xl">
              <div className="text-2xl font-bold text-blue-400">{roadmap.summary.beta}</div>
              <div className="text-sm text-muted-foreground">In Beta</div>
            </div>
            <div className="p-4 bg-card border border-border rounded-xl">
              <div className="text-2xl font-bold text-yellow-400">{roadmap.summary.in_progress}</div>
              <div className="text-sm text-muted-foreground">In Progress</div>
            </div>
            <div className="p-4 bg-card border border-border rounded-xl">
              <div className="text-2xl font-bold text-muted-foreground">{roadmap.summary.planned}</div>
              <div className="text-sm text-muted-foreground">Planned</div>
            </div>
          </div>
        )}
        
        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search features..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-card"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="p-2 bg-card border border-border rounded-lg text-sm"
            >
              <option value="all">All Status</option>
              <option value="released">Released</option>
              <option value="beta">Beta</option>
              <option value="in_progress">In Progress</option>
              <option value="planned">Planned</option>
            </select>
          </div>
        </div>
        
        {/* Admin Notice */}
        {user?.is_founder && (
          <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-center gap-3">
            <Settings className="w-5 h-5 text-amber-400" />
            <div className="text-sm">
              <span className="font-semibold text-amber-400">Admin Mode:</span>
              <span className="text-muted-foreground ml-2">
                You can toggle feature flags and visibility. Click the play/pause icon to enable/disable, 
                or the eye icon to show/hide from users.
              </span>
            </div>
          </div>
        )}
        
        {/* Phases */}
        {roadmap?.phases && Object.entries(roadmap.phases).map(([phaseId, phase]) => (
          <PhaseSection
            key={phaseId}
            phaseId={phaseId}
            phase={phase}
            features={roadmap.features_by_phase[phaseId] || []}
          />
        ))}
      </div>
      </div>
    </AppShell>
  );
};

export default RoadmapPage;
